import { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { attendanceAPI, qrAPI, usersAPI } from '../../lib/api';
import { toast } from 'sonner';
import { QrCode, Camera, Check, X, AlertTriangle, RefreshCw, Search, ShieldCheck, Volume2, VolumeX, Clock, Ban, CalendarClock, Maximize2, MapPin, Lock, Delete } from 'lucide-react';
import jsQR from 'jsqr';

// Sinteza vocală românească citește cifra „2" ca „doi" (masculin) și „1" ca „unu".
// Pentru substantive feminine („ședință", „intrare") trebuie „două" / „o".
// Convertim numărul în litere, la forma feminină, ca vocea să fie corectă.
function roNumberFeminine(value) {
  const n = Math.round(Number(value));
  if (!isFinite(n) || n < 0) return String(value);
  const units = ['zero', 'una', 'două', 'trei', 'patru', 'cinci', 'șase', 'șapte', 'opt', 'nouă'];
  const teens = ['zece', 'unsprezece', 'douăsprezece', 'treisprezece', 'paisprezece', 'cincisprezece', 'șaisprezece', 'șaptesprezece', 'optsprezece', 'nouăsprezece'];
  const tens = ['', '', 'douăzeci', 'treizeci', 'patruzeci', 'cincizeci', 'șaizeci', 'șaptezeci', 'optzeci', 'nouăzeci'];
  if (n < 10) return units[n];
  if (n < 20) return teens[n - 10];
  if (n < 100) {
    const t = Math.floor(n / 10);
    const u = n % 10;
    return u === 0 ? tens[t] : `${tens[t]} și ${units[u]}`;
  }
  return String(n);
}

// Fragment vocal pentru un substantiv feminin numărabil, cu acord corect.
// Ex: 1 → „o ședință", 2 → „două ședințe", 21 → „douăzeci și una de ședințe".
function roCountFeminine(n, singular, plural) {
  if (n === 1) return `o ${singular}`;
  const word = n >= 20 ? `de ${plural}` : plural;
  return `${roNumberFeminine(n)} ${word}`;
}

// Construiește mesajul de feedback (vizual + voce) dintr-un răspuns de succes
function buildSuccessFeedback(data) {
  const firstName = (data.person_name || '').trim().split(/\s+/)[0] || data.person_name;
  const n = data.sessions_remaining;
  const extra = (data.warnings || []).find((w) => /expir/i.test(w));
  const extraVoice = extra ? ` ${extra}` : '';

  if (n != null && n <= 0) {
    return {
      variant: 'warning',
      icon: 'check',
      name: data.person_name,
      headline: 'Ultima ședință',
      sub: 'Abonament finalizat',
      voice: `Bun venit, ${firstName}. Aceasta a fost ultima ședință din abonament.${extraVoice}`,
    };
  }

  if (n != null) {
    const word = n === 1 ? 'ședință' : 'ședințe';
    const headline = n === 1 ? 'Mai este 1 ședință' : `Mai sunt ${n} ${word}`;
    const lowVoice = n <= 2 ? ' Atenție, abonamentul se apropie de final.' : '';
    return {
      variant: n <= 2 ? 'warning' : 'success',
      icon: 'check',
      name: data.person_name,
      headline,
      sub: extra || null,
      voice: `Bun venit, ${firstName}. Mai ${n === 1 ? 'este' : 'sunt'} ${roCountFeminine(n, 'ședință', 'ședințe')}.${lowVoice}${extraVoice}`,
    };
  }

  return {
    variant: 'success',
    icon: 'check',
    name: data.person_name,
    headline: 'Acces permis',
    sub: extra || 'Bun venit!',
    voice: `Bun venit, ${firstName}. Acces permis.${extraVoice}`,
  };
}

// ── Mod TEST ─────────────────────────────────────────────────────────────
// Token-uri demo (din pagina /test-qrs.html) care declanșează fiecare voce
// fără a depinde de baza de date sau de ora curentă.
const TEST_PREFIX = 'NAUTICA-TEST:';
function getTestOutcome(token) {
  if (!token || !token.startsWith(TEST_PREFIX)) return null;
  const parts = token.slice(TEST_PREFIX.length).split(':');
  const kind = parts[0];
  const num = parseInt(parts[1], 10);
  const nameFrom = (i) => parts.slice(i).join(':') || 'Test Înotător';

  switch (kind) {
    case 'success':
    case 'low':
    case 'one':
    case 'last':
      return { ok: true, data: { person_name: nameFrom(2), person_type: 'child', sessions_remaining: num, sessions_total: Math.max(num, 12), warnings: null } };
    case 'unlimited':
      return { ok: true, data: { person_name: nameFrom(1), person_type: 'user', sessions_remaining: null, sessions_total: null, warnings: null } };
    case 'expiring':
      return { ok: true, data: { person_name: nameFrom(2), person_type: 'user', sessions_remaining: num, sessions_total: Math.max(num, 12), warnings: ['Abonamentul expiră în 3 zile.'] } };
    case 'expired':
      return { ok: false, detail: 'Persoana nu are niciun abonament activ. Acces Respins.' };
    case 'outside':
      return { ok: false, detail: 'Scanare în afara intervalului orar permis.' };
    case 'already':
      return { ok: false, detail: 'Prezență deja înregistrată în această zi.' };
    case 'sunday':
      return { ok: false, detail: 'Prezența nu se poate înregistra duminică.' };
    case 'invalid':
    default:
      return { ok: false, detail: 'Cod QR invalid sau utilizator șters.' };
  }
}

// Construiește feedback-ul pentru un mesaj de eroare/refuz
function buildErrorFeedback(detail) {
  const d = (detail || '').toLowerCase();
  if (d.includes('abonament activ') || d.includes('expirat')) {
    return { variant: 'error', icon: 'ban', headline: 'Abonament expirat', sub: 'Abonamentul se poate reînnoi la recepție', voice: 'Abonament expirat. Acces respins.' };
  }
  if (d.includes('afara intervalului') || d.includes('în afara') || d.includes('orar')) {
    return { variant: 'error', icon: 'clock', headline: 'În afara programului', sub: 'Program: 07:00 - 21:00', voice: 'Acces respins. Scanare în afara programului.' };
  }
  if (d.includes('deja înregistrat')) {
    return { variant: 'warning', icon: 'calendar', headline: 'Prezență deja marcată', sub: 'Înregistrată deja astăzi', voice: 'Prezență deja înregistrată astăzi.' };
  }
  if (d.includes('duminic')) {
    return { variant: 'warning', icon: 'calendar', headline: 'Închis duminica', sub: 'Prezența nu se înregistrează duminica', voice: 'Prezența nu se înregistrează duminica.' };
  }
  if (d.includes('invalid')) {
    return { variant: 'error', icon: 'x', headline: 'Cod QR invalid', sub: 'Codul nu a putut fi citit', voice: 'Cod QR invalid.' };
  }
  return { variant: 'error', icon: 'x', headline: 'Acces respins', sub: detail, voice: detail };
}

// Locațiile statice (la fel ca în restul aplicației)
const LOCATIONS = [
  { id: "fiald", name: 'Bazin Fiald (Hotel Fiald)' },
  { id: "olimpic", name: 'Bazinul Olimpic Bacău' },
  { id: "emd", name: 'Bazinul EMD Academy' }
];

const VARIANTS = {
  success: { color: '#6db025', tint: 'rgba(109,176,37,0.10)', label: 'Acces permis' },
  warning: { color: '#facc15', tint: 'rgba(250,204,21,0.09)', label: 'Atenție' },
  error:   { color: '#ef4444', tint: 'rgba(239,68,68,0.10)',  label: 'Acces respins' },
};

const FeedbackIcon = ({ name, color }) => {
  const common = { size: 64, strokeWidth: 2.25, color };
  switch (name) {
    case 'ban': return <Ban {...common} />;
    case 'clock': return <Clock {...common} />;
    case 'calendar': return <CalendarClock {...common} />;
    case 'x': return <X {...common} />;
    default: return <Check {...common} />;
  }
};

// Ecran de rezultat afișat la fiecare scanare; dispare singur după ~5s.
// Culoarea întregului ecran (verde / galben / roșu) se vede și de la distanță.
const ScanFeedbackOverlay = ({ feedback, onDismiss }) => {
  if (!feedback) return null;
  const v = VARIANTS[feedback.variant] || VARIANTS.success;

  return createPortal(
    <div
      key={feedback.key}
      onClick={onDismiss}
      className={`fixed inset-0 z-[120] flex flex-col items-center justify-center px-6 cursor-pointer bg-[#07090b] ${feedback.leaving ? 'kiosk-feedback-out' : 'kiosk-feedback-in'}`}
    >
      <div className="absolute inset-0 pointer-events-none" style={{ background: v.tint }} />
      <div className="absolute top-0 inset-x-0 h-1.5" style={{ background: v.color }} />

      <div className="relative text-center max-w-2xl">
        <div
          className="mx-auto mb-8 w-32 h-32 rounded-full flex items-center justify-center"
          style={{ background: v.color + '1f', border: `2px solid ${v.color}` }}
        >
          <FeedbackIcon name={feedback.icon} color={v.color} />
        </div>

        <p className="text-sm font-bold uppercase tracking-[0.3em] mb-4" style={{ color: v.color }}>
          {v.label}
        </p>

        {feedback.name && (
          <h2 className="font-heading text-5xl sm:text-6xl font-black text-white uppercase leading-none mb-4">
            {feedback.name}
          </h2>
        )}

        <p className="font-heading text-3xl sm:text-4xl font-bold uppercase tracking-tight" style={{ color: v.color }}>
          {feedback.headline}
        </p>

        {feedback.sub && (
          <p className="text-white/60 text-lg sm:text-xl mt-3">{feedback.sub}</p>
        )}
      </div>

      {/* Timp rămas până la închidere (animat doar prin transform) */}
      {!feedback.leaving && (
        <div className="absolute bottom-0 inset-x-0 h-1 bg-white/5">
          <div className="h-full w-full kiosk-progress" style={{ background: v.color }} />
        </div>
      )}
    </div>,
    document.body,
  );
};

// Ceas live afișat în modul kiosk
const KioskClock = () => {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  const time = now.toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit' });
  const date = now.toLocaleDateString('ro-RO', { weekday: 'long', day: 'numeric', month: 'long' });
  return (
    <div className="text-right">
      <p className="font-heading text-3xl sm:text-4xl font-black text-white tabular-nums leading-none">{time}</p>
      <p className="text-white/40 text-[11px] uppercase font-black tracking-[0.25em] mt-1 capitalize">{date}</p>
    </div>
  );
};

// Anti-dublă-scanare: scanner-ul fizic citește același cod de mai multe ori când îl
// plimbi peste QR. Acceptăm o singură scanare la fiecare SCAN_MIN_GAP_MS, iar același
// cod e ignorat dacă e re-scanat în fereastra SCAN_DUP_WINDOW_MS.
const SCAN_MIN_GAP_MS = 1500;     // pauză minimă între orice două scanări acceptate
const SCAN_DUP_WINDOW_MS = 4000;  // același cod e ignorat dacă reapare în acest interval

// Cod de acces pentru a ieși din modul Kiosk (împiedică clienții să închidă ecranul)
const KIOSK_EXIT_CODE = '2026';
const KIOSK_EXIT_IDLE_MS = 5000; // auto-închidere a tastaturii dacă nu se tastează nimic

// Modul KIOSK - ecran complet pentru tabletă, alimentat de un SCANNER FIZIC de
// coduri QR legat la tabletă (input tip „keyboard wedge"). Fără cameră.
const KioskView = ({ scanning, selectedLocation, voiceEnabled, onToggleVoice, onExit, onLockChange }) => {
  // Tastatura de cod pentru ieșire (protejează kiosk-ul de clienți)
  const [exitPrompt, setExitPrompt] = useState(false);
  const [code, setCode] = useState('');
  const [codeError, setCodeError] = useState(false);
  const idleTimerRef = useRef(null);

  const clearIdle = useCallback(() => {
    if (idleTimerRef.current) { clearTimeout(idleTimerRef.current); idleTimerRef.current = null; }
  }, []);

  const closePrompt = useCallback(() => {
    clearIdle();
    setExitPrompt(false);
    setCode('');
    setCodeError(false);
    onLockChange?.(false);
  }, [clearIdle, onLockChange]);

  // Resetează cronometrul de inactivitate (auto-închidere după 5s fără tastare)
  const armIdle = useCallback(() => {
    clearIdle();
    idleTimerRef.current = setTimeout(closePrompt, KIOSK_EXIT_IDLE_MS);
  }, [clearIdle, closePrompt]);

  const openPrompt = useCallback(() => {
    setCode('');
    setCodeError(false);
    setExitPrompt(true);
    onLockChange?.(true); // suspendă scanner-ul cât timp se tastează codul
    armIdle();
  }, [armIdle, onLockChange]);

  const pressDigit = useCallback((d) => {
    setCodeError(false);
    armIdle();
    setCode((prev) => (prev.length >= 4 ? prev : prev + d));
  }, [armIdle]);

  const backspace = useCallback(() => {
    setCodeError(false);
    armIdle();
    setCode((prev) => prev.slice(0, -1));
  }, [armIdle]);

  // Validează codul când ajunge la 4 cifre (efect separat, fără side-effects în updater)
  useEffect(() => {
    if (!exitPrompt || code.length < 4) return;
    if (code === KIOSK_EXIT_CODE) {
      clearIdle();
      onLockChange?.(false);
      onExit();
    } else {
      setCodeError(true);
      const t = setTimeout(() => setCode(''), 700); // golește pentru reîncercare
      return () => clearTimeout(t);
    }
  }, [code, exitPrompt, clearIdle, onExit, onLockChange]);

  // Curăță cronometrul la demontare
  useEffect(() => clearIdle, [clearIdle]);

  return (
    <div className="fixed inset-0 z-[110] bg-[#07090b] text-white overflow-hidden animate-fade-in select-none">
      {/* Fundal static, discret (fără animații) */}
      <div className="absolute inset-0 pointer-events-none"
           style={{ background: 'radial-gradient(ellipse at 50% 45%, rgba(109,176,37,0.07), transparent 60%)' }} />

      {/* Bară de sus: brand + ceas */}
      <div className="absolute top-0 inset-x-0 flex items-center justify-between px-6 sm:px-10 py-6 z-20 border-b border-white/[0.06]">
        <div className="flex items-center gap-4">
          <img src="/nautica-logo.png" alt="Nautica" className="h-12 sm:h-14 w-auto object-contain" />
          <div>
            <p className="font-heading text-2xl sm:text-3xl font-black uppercase tracking-tight leading-none">Nautica</p>
            <p className="text-white/40 text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.3em] mt-1">Acces bazin</p>
          </div>
        </div>
        <KioskClock />
      </div>

      {/* Centru */}
      <div className="absolute inset-0 flex flex-col items-center justify-center px-6">
        <div className="relative w-[min(60vw,300px)] aspect-square rounded-3xl bg-white/[0.02] border border-white/10 flex items-center justify-center mb-10 sm:mb-12">
          <QrCode size={150} strokeWidth={1.25} className="text-[#6db025]" />

          {/* Colțuri statice */}
          <span className="absolute top-4 left-4 w-8 h-8 border-t-2 border-l-2 border-[#6db025]/60 rounded-tl-lg" />
          <span className="absolute top-4 right-4 w-8 h-8 border-t-2 border-r-2 border-[#6db025]/60 rounded-tr-lg" />
          <span className="absolute bottom-4 left-4 w-8 h-8 border-b-2 border-l-2 border-[#6db025]/60 rounded-bl-lg" />
          <span className="absolute bottom-4 right-4 w-8 h-8 border-b-2 border-r-2 border-[#6db025]/60 rounded-br-lg" />

          {scanning && (
            <div className="absolute inset-0 rounded-3xl flex flex-col items-center justify-center gap-3 bg-[#07090b]/85">
              <RefreshCw size={28} className="text-[#6db025] animate-spin" />
              <p className="text-white/60 text-[11px] uppercase font-bold tracking-[0.3em]">Se verifică...</p>
            </div>
          )}
        </div>

        <div className="text-center max-w-xl">
          <h1 className="font-heading text-4xl sm:text-6xl font-black uppercase tracking-tight leading-none mb-4">
            Scanează codul QR
          </h1>
          <p className="text-white/50 text-base sm:text-lg">
            Apropie cardul cu codul QR de scanner pentru a înregistra prezența
          </p>

          {/* Status */}
          <div className="inline-flex items-center gap-2.5 mt-8 text-white/50">
            <span className={`w-2 h-2 rounded-full ${scanning ? 'bg-yellow-400' : 'bg-[#6db025] kiosk-dot'}`} />
            <span className="text-[11px] font-bold uppercase tracking-[0.3em]">
              {scanning ? 'Se procesează' : 'Scanner pregătit'}
            </span>
          </div>
        </div>
      </div>

      {/* Bară de jos: locație + controale */}
      <div className="absolute bottom-0 inset-x-0 flex items-center justify-between gap-4 px-6 sm:px-10 py-6 z-20 border-t border-white/[0.06]">
        <div className="flex items-center gap-2.5 text-white/40">
          <MapPin size={16} className="text-[#6db025]" />
          <span className="text-[11px] font-bold uppercase tracking-[0.25em] truncate max-w-[40vw]">{selectedLocation}</span>
        </div>
        <div className="flex items-center gap-2.5">
          <button
            onClick={onToggleVoice}
            title={voiceEnabled ? 'Dezactivează vocea' : 'Activează vocea'}
            className={`flex items-center justify-center w-12 h-12 rounded-full border transition-colors ${
              voiceEnabled ? 'bg-[#6db025]/15 text-[#6db025] border-[#6db025]/30' : 'bg-white/5 text-white/40 border-white/10'
            }`}
          >
            {voiceEnabled ? <Volume2 size={18} /> : <VolumeX size={18} />}
          </button>
          <button
            onClick={openPrompt}
            className="flex items-center gap-2 px-5 h-12 rounded-full bg-white/5 text-white/60 border border-white/10 hover:text-white hover:bg-red-500/15 hover:border-red-500/30 transition-colors text-[11px] font-black uppercase tracking-widest"
          >
            <X size={16} /> <span className="hidden sm:inline">Ieși din kiosk</span>
          </button>
        </div>
      </div>

      {/* Tastatură cod de acces pentru ieșire */}
      {exitPrompt && (
        <div
          onClick={closePrompt}
          className="absolute inset-0 z-30 flex items-center justify-center p-6 animate-fade-in"
          style={{ background: 'rgba(7,9,11,0.94)' }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className={`w-full max-w-xs bg-[#0a0f0c] border rounded-2xl p-7 text-center ${codeError ? 'border-red-500/50 animate-shake' : 'border-[#6db025]/25'}`}
            style={{ boxShadow: '0 0 60px rgba(0,0,0,0.6)' }}
          >
            <div className="w-14 h-14 mx-auto mb-4 rounded-full flex items-center justify-center bg-[#6db025]/10 border border-[#6db025]/30">
              <Lock size={24} className="text-[#6db025]" />
            </div>
            <h3 className="font-heading text-2xl font-black uppercase tracking-tight">Cod de acces</h3>
            <p className="text-white/40 text-xs mt-1 mb-5">
              {codeError ? <span className="text-red-400 font-bold uppercase tracking-widest">Cod greșit</span> : 'Introdu codul pentru a ieși'}
            </p>

            {/* Indicatoare cifre */}
            <div className="flex items-center justify-center gap-3 mb-6">
              {[0, 1, 2, 3].map((i) => (
                <span
                  key={i}
                  className={`w-4 h-4 rounded-full border transition-all ${
                    codeError ? 'border-red-500/60' : 'border-[#6db025]/40'
                  } ${i < code.length ? (codeError ? 'bg-red-500' : 'bg-[#6db025]') : 'bg-transparent'}`}
                />
              ))}
            </div>

            {/* Tastatura numerică */}
            <div className="grid grid-cols-3 gap-2.5">
              {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((d) => (
                <button
                  key={d}
                  onClick={() => pressDigit(d)}
                  className="h-14 rounded-xl bg-white/[0.04] border border-white/10 text-white text-2xl font-heading font-bold hover:bg-[#6db025]/15 hover:border-[#6db025]/40 active:scale-95 transition-all"
                >
                  {d}
                </button>
              ))}
              <button
                onClick={closePrompt}
                className="h-14 rounded-xl bg-white/[0.02] border border-white/5 text-white/40 text-[10px] font-black uppercase tracking-widest hover:text-white hover:bg-white/5 active:scale-95 transition-all"
              >
                Anulează
              </button>
              <button
                onClick={() => pressDigit('0')}
                className="h-14 rounded-xl bg-white/[0.04] border border-white/10 text-white text-2xl font-heading font-bold hover:bg-[#6db025]/15 hover:border-[#6db025]/40 active:scale-95 transition-all"
              >
                0
              </button>
              <button
                onClick={backspace}
                className="h-14 rounded-xl bg-white/[0.02] border border-white/5 text-white/50 flex items-center justify-center hover:text-white hover:bg-white/5 active:scale-95 transition-all"
              >
                <Delete size={20} />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Pagină de scanare QR - kiosk cu feedback vizual animat și voce în română
const QRScanPage = () => {
  const [selectedLocation, setSelectedLocation] = useState(LOCATIONS[0].name);
  const [qrInput, setQrInput] = useState('');
  const [scanning, setScanning] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  
  const [mode, setMode] = useState('manual'); // 'manual' | 'camera' | 'verify'
  const cameraMode = mode === 'camera';
  const [cameraActive, setCameraActive] = useState(false);
  const [cameraError, setCameraError] = useState('');

  // Mod KIOSK (ecran complet pentru tabletă la intrare)
  const [kiosk, setKiosk] = useState(false);

  // Feedback kiosk (overlay animat + voce)
  const [feedback, setFeedback] = useState(null);
  const [voiceEnabled, setVoiceEnabled] = useState(true);
  const voiceEnabledRef = useRef(true);
  useEffect(() => { voiceEnabledRef.current = voiceEnabled; }, [voiceEnabled]);
  const feedbackTimers = useRef([]);

  // Pre-încarcă vocile de sinteză (necesare pe unele browsere)
  useEffect(() => {
    const synth = window.speechSynthesis;
    if (!synth) return;
    const load = () => synth.getVoices();
    load();
    synth.addEventListener?.('voiceschanged', load);
    return () => synth.removeEventListener?.('voiceschanged', load);
  }, []);

  const speak = useCallback((text) => {
    try {
      const synth = window.speechSynthesis;
      if (!synth || !text || !voiceEnabledRef.current) return;
      synth.cancel();
      const utter = new SpeechSynthesisUtterance(text);
      utter.lang = 'ro-RO';
      utter.rate = 0.98;
      utter.pitch = 1.0;
      utter.volume = 1;
      const voices = synth.getVoices();
      const ro = voices.find((v) => (v.lang || '').toLowerCase().startsWith('ro'));
      if (ro) utter.voice = ro;
      synth.speak(utter);
    } catch {
      /* ignore */
    }
  }, []);

  const showFeedback = useCallback((fb) => {
    feedbackTimers.current.forEach(clearTimeout);
    feedbackTimers.current = [];
    setFeedback({ ...fb, leaving: false, key: Date.now() });
    speak(fb.voice);
    // Pornește ieșirea cu fade după ~5.2s, apoi curăță
    feedbackTimers.current.push(
      setTimeout(() => setFeedback((f) => (f ? { ...f, leaving: true } : null)), 5200),
    );
    feedbackTimers.current.push(setTimeout(() => setFeedback(null), 5800));
  }, [speak]);

  useEffect(() => () => feedbackTimers.current.forEach(clearTimeout), []);

  // Verifică Abonament
  const [verifySearch, setVerifySearch] = useState('');
  const [verifyResults, setVerifyResults] = useState([]);
  const [verifyLoading, setVerifyLoading] = useState(false);
  const [selectedResult, setSelectedResult] = useState(null);
  
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const scanIntervalRef = useRef(null);
  const canvasRef = useRef(null);
  // Refs pentru a evita recrearea callback-urilor când se schimbă starea
  const scanningRef = useRef(false);
  const lastScanRef = useRef(0);
  const lastTokenRef = useRef(''); // ultimul cod procesat (pentru anti-dublă-scanare)
  const selectedLocationRef = useRef(selectedLocation);
  useEffect(() => { selectedLocationRef.current = selectedLocation; }, [selectedLocation]);

  // Funcția principală de procesare a token-ului
  const processQrToken = useCallback(async (token, method = 'qr') => {
    if (!token || scanningRef.current) return;

    // Anti-dublă-scanare: ignoră reluările rapide ale scanner-ului fizic
    const trimmed = token.trim();
    const sinceLast = Date.now() - lastScanRef.current;
    if (sinceLast < SCAN_MIN_GAP_MS) return;
    if (trimmed === lastTokenRef.current && sinceLast < SCAN_DUP_WINDOW_MS) return;
    lastTokenRef.current = trimmed;

    scanningRef.current = true;
    setScanning(true);
    setLastResult(null);

    // Mod TEST: token-uri demo pentru a testa fiecare voce / variantă vizuală
    const testOutcome = getTestOutcome(trimmed);
    if (testOutcome) {
      if (testOutcome.ok) {
        setLastResult({ success: true, ...testOutcome.data, method: 'test' });
        showFeedback(buildSuccessFeedback(testOutcome.data));
      } else {
        setLastResult({ success: false, error: testOutcome.detail });
        showFeedback(buildErrorFeedback(testOutcome.detail));
      }
      scanningRef.current = false;
      setScanning(false);
      lastScanRef.current = Date.now();
      setQrInput('');
      return;
    }

    try {
      const response = await attendanceAPI.recordQR(trimmed, selectedLocationRef.current);
      setLastResult({ success: true, ...response.data, method });
      showFeedback(buildSuccessFeedback(response.data));
      setQrInput('');
    } catch (error) {
      const message = error.response?.data?.detail || 'Cod QR invalid';
      setLastResult({ success: false, error: message });
      showFeedback(buildErrorFeedback(message));
    } finally {
      scanningRef.current = false;
      setScanning(false);
      // Cooldown de 2s înainte de a scana din nou același cod
      lastScanRef.current = Date.now();
    }
  }, [showFeedback]);

  // Funcția de oprire a camerei - stabilă (fără dependențe)
  const stopCamera = useCallback(() => {
    if (scanIntervalRef.current) {
      clearInterval(scanIntervalRef.current);
      scanIntervalRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    setCameraActive(false);
  }, []);

  // Funcția de scanare - nu depinde de state, folosește ref-uri
  const scanFromVideoFrame = useCallback(() => {
    const video = videoRef.current;
    if (!video || video.readyState < 2) return;
    if (scanningRef.current) return;
    if (Date.now() - lastScanRef.current < 2000) return; // cooldown

    if (!canvasRef.current) {
      canvasRef.current = document.createElement('canvas');
    }
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const code = jsQR(imageData.data, imageData.width, imageData.height);
    if (code?.data) {
      processQrToken(code.data, 'camera');
    }
  }, [processQrToken]);

  // Funcția de pornire a camerei - depinde doar de funcții stabile
  const startCamera = useCallback(async () => {
    setCameraError('');
    setLastResult(null);

    if (!navigator.mediaDevices?.getUserMedia) {
      setCameraError('Camera nu este disponibilă în acest browser/dispozitiv.');
      return;
    }

    try {
      stopCamera();
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'environment' } },
        audio: false,
      });

      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setCameraActive(true);
      scanIntervalRef.current = setInterval(scanFromVideoFrame, 300);
    } catch (error) {
      if (error.name !== 'AbortError') {
        setCameraError('Nu am putut porni camera. Verifică permisiunile browserului.');
      }
      stopCamera();
    }
  }, [scanFromVideoFrame, stopCamera]);

  // Pornire/oprire cameră doar în tab-ul „Cameră Telefon"
  useEffect(() => {
    if (mode === 'camera') {
      startCamera();
    } else {
      stopCamera();
    }
    return () => stopCamera();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  // Modul Kiosk folosește un scanner FIZIC (tip „keyboard wedge"): scanner-ul
  // „tastează" token-ul și trimite Enter. Capturăm tastele la nivel de fereastră.
  const scanBufferRef = useRef('');
  const scanBufferTimeRef = useRef(0);
  const kioskLockedRef = useRef(false); // true cât timp tastatura de cod e deschisă
  useEffect(() => {
    if (!kiosk) return;
    const onKeyDown = (e) => {
      if (kioskLockedRef.current) return; // ignoră scanner-ul cât timp se tastează codul
      const now = Date.now();
      // Dacă a trecut prea mult de la ultima tastă, e tastare umană → resetăm
      if (now - scanBufferTimeRef.current > 800) scanBufferRef.current = '';
      scanBufferTimeRef.current = now;

      if (e.key === 'Enter') {
        const token = scanBufferRef.current.trim();
        scanBufferRef.current = '';
        if (token) processQrToken(token, 'scanner');
        e.preventDefault();
        return;
      }
      if (e.key.length === 1) scanBufferRef.current += e.key;
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [kiosk, processQrToken]);

  // Menține ecranul aprins cât timp kiosk-ul e activ (Screen Wake Lock API).
  // Necesită context securizat (HTTPS sau localhost); unde nu e disponibil, ignorăm.
  // Lock-ul se pierde automat când tab-ul devine invizibil / tableta e blocată,
  // așa că îl recerem la fiecare revenire pe pagină.
  const wakeLockRef = useRef(null);
  useEffect(() => {
    if (!kiosk) return;

    let cancelled = false;
    const acquire = async () => {
      try {
        if (!navigator.wakeLock || document.visibilityState !== 'visible') return;
        const lock = await navigator.wakeLock.request('screen');
        if (cancelled) { lock.release().catch(() => {}); return; }
        wakeLockRef.current = lock;
      } catch { /* nesuportat sau blocat de sistem - ignorăm */ }
    };

    const onVisibility = () => {
      if (document.visibilityState === 'visible') acquire();
    };

    acquire();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisibility);
      wakeLockRef.current?.release().catch(() => {});
      wakeLockRef.current = null;
    };
  }, [kiosk]);

  // Intră/ieși din modul Kiosk (cu full-screen real al browserului)
  const enterKiosk = useCallback(() => {
    setMode('manual'); // oprește camera dacă era pornită - kiosk-ul folosește scanner fizic
    setKiosk(true);
    setLastResult(null);
    try { document.documentElement.requestFullscreen?.(); } catch { /* ignore */ }
  }, []);

  const exitKiosk = useCallback(() => {
    setKiosk(false);
    try { if (document.fullscreenElement) document.exitFullscreen?.(); } catch { /* ignore */ }
  }, []);

  // Sincronizează starea kiosk dacă utilizatorul iese din full-screen (ex. tasta Esc)
  useEffect(() => {
    const onFsChange = () => { if (!document.fullscreenElement) setKiosk(false); };
    document.addEventListener('fullscreenchange', onFsChange);
    return () => document.removeEventListener('fullscreenchange', onFsChange);
  }, []);

  const handleVerifySearch = useCallback(async (query) => {
    setVerifySearch(query);
    setSelectedResult(null);
    if (!query.trim() || query.trim().length < 2) {
      setVerifyResults([]);
      return;
    }
    setVerifyLoading(true);
    try {
      const res = await usersAPI.getWithActiveSubscriptions({ search: query.trim() });
      setVerifyResults(res.data || []);
    } catch {
      setVerifyResults([]);
    } finally {
      setVerifyLoading(false);
    }
  }, []);

  const handleSubmitManual = async (e) => {
    e.preventDefault();
    if (!qrInput.trim()) return;
    await processQrToken(qrInput.trim(), 'qr');
  };

  const handleValidateOnly = async () => {
    if (!qrInput.trim()) return;
    try {
      const response = await qrAPI.validate(qrInput.trim());
      if (response.data.valid) {
        toast.success(`QR Valid: Aparține lui ${response.data.person_name}`);
      } else {
        toast.error('Cod QR invalid sau expirat');
      }
    } catch (error) {
      toast.error('Eroare la validare');
    }
  };

  return (
    <div className="space-y-6">
      <ScanFeedbackOverlay feedback={feedback} onDismiss={() => setFeedback(null)} />

      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-heading text-4xl font-bold text-white uppercase tracking-tight">Scanare QR</h1>
          <p className="text-white/40 text-base mt-1">Înregistrare rapidă a prezenței la intrarea în bazin</p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            onClick={() => setVoiceEnabled((v) => !v)}
            title={voiceEnabled ? 'Dezactivează vocea' : 'Activează vocea'}
            className={`flex items-center gap-2 px-4 py-2.5 rounded text-xs font-black uppercase tracking-widest transition-colors ${
              voiceEnabled ? 'bg-[#6db025]/15 text-[#6db025] border border-[#6db025]/30' : 'bg-white/5 text-white/40 border border-white/10'
            }`}
          >
            {voiceEnabled ? <Volume2 size={16} /> : <VolumeX size={16} />}
            <span className="hidden sm:inline">{voiceEnabled ? 'Voce pornită' : 'Voce oprită'}</span>
          </button>
          <button
            onClick={enterKiosk}
            title="Pornește modul Kiosk pe acest ecran"
            className="flex items-center gap-2 px-4 py-2.5 rounded text-xs font-black uppercase tracking-widest transition-all bg-[#6db025] text-black hover:shadow-[0_0_20px_rgba(109,176,37,0.45)] hover:scale-[1.02]"
          >
            <Maximize2 size={16} />
            <span className="hidden sm:inline">Mod Kiosk</span>
          </button>
        </div>
      </div>

      {/* Tabs / Selectors */}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => { setMode('manual'); setLastResult(null); }}
          className={`flex items-center gap-2 px-4 py-2 text-xs font-black uppercase tracking-widest transition-colors rounded ${
            mode === 'manual' ? 'bg-[#6db025] text-black' : 'bg-white/5 text-white/40 hover:bg-white/10'
          }`}
        >
          <QrCode size={14} /> Manual
        </button>
        <button
          onClick={() => { setMode('camera'); setLastResult(null); }}
          className={`flex items-center gap-2 px-4 py-2 text-xs font-black uppercase tracking-widest transition-colors rounded ${
            mode === 'camera' ? 'bg-[#6db025] text-black' : 'bg-white/5 text-white/40 hover:bg-white/10'
          }`}
        >
          <Camera size={14} /> Cameră Telefon
        </button>
        <button
          onClick={() => { setMode('verify'); setLastResult(null); setVerifyResults([]); setVerifySearch(''); setSelectedResult(null); }}
          className={`flex items-center gap-2 px-4 py-2 text-xs font-black uppercase tracking-widest transition-colors rounded ${
            mode === 'verify' ? 'bg-[#6db025] text-black' : 'bg-white/5 text-white/40 hover:bg-white/10'
          }`}
        >
          <ShieldCheck size={14} /> Verifică Abonament
        </button>
      </div>

      {/* Verifică Abonament Panel */}
      {mode === 'verify' && (
        <div className="bg-[#0A0A0A] border border-white/5 p-6 space-y-5">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-white/30" size={18} />
            <input
              type="text"
              value={verifySearch}
              onChange={(e) => handleVerifySearch(e.target.value)}
              placeholder="Caută după numele clientului sau al copilului..."
              className="w-full bg-[#121212] border border-white/10 focus:border-[#6db025] pl-12 pr-4 py-4 text-white placeholder:text-white/20 outline-none text-sm"
              autoFocus
            />
          </div>

          {verifyLoading && (
            <p className="text-white/30 text-[10px] uppercase font-black tracking-widest animate-pulse">Se caută...</p>
          )}

          {!verifyLoading && verifySearch.length >= 2 && verifyResults.length === 0 && (
            <div className="p-6 text-center text-white/20 text-sm font-bold uppercase tracking-widest border border-white/5">
              Niciun abonament activ găsit pentru "{verifySearch}"
            </div>
          )}

          {verifyResults.length > 0 && !selectedResult && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {verifyResults.map((item, i) => (
                <button
                  key={i}
                  onClick={() => setSelectedResult(item)}
                  className="relative bg-[#0F0F0F] border border-white/5 hover:border-white/10 hover:bg-[#121212] transition-colors rounded overflow-hidden group text-left"
                >
                  <div className="absolute left-0 top-0 bottom-0 w-[3px] bg-[#6db025]" />
                  <div className="p-4 pl-5 flex items-center justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <p className="text-white font-bold uppercase text-base truncate">{item.person_name}</p>
                      <p className="text-white/50 text-[11px] uppercase font-black tracking-widest mt-1 truncate">{item.subscription?.plan_name}</p>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {item.subscription?.sessions_remaining != null && (
                        <span className={`text-sm font-black ${item.subscription.sessions_remaining <= 2 ? 'text-yellow-400' : 'text-[#6db025]'}`}>
                          {item.subscription.sessions_remaining} ședințe
                        </span>
                      )}
                      <Check size={18} className="text-[#6db025] opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}

          {selectedResult && (
            <div className="space-y-4">
              <button onClick={() => setSelectedResult(null)} className="text-white/30 hover:text-white text-[10px] uppercase font-black tracking-widest flex items-center gap-1">
                ← Înapoi la rezultate
              </button>
              <div className="p-6 bg-[#6db025]/5 border border-[#6db025]/20 space-y-4">
                <div className="flex items-center gap-3">
                  <ShieldCheck size={20} className="text-[#6db025]" />
                  <div>
                    <p className="text-white font-heading font-bold text-xl uppercase">{selectedResult.person_name}</p>
                    <p className="text-white/40 text-[10px] uppercase font-black tracking-widest">
                      {selectedResult.person_type === 'child' ? 'Copil' : 'Adult / Titular'}
                    </p>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 bg-white/[0.03] border border-white/5">
                    <p className="text-white/30 text-[10px] uppercase font-black tracking-widest mb-1">Program</p>
                    <p className="text-white font-bold text-sm">{selectedResult.subscription?.plan_name}</p>
                  </div>
                  <div className="p-3 bg-white/[0.03] border border-white/5">
                    <p className="text-white/30 text-[10px] uppercase font-black tracking-widest mb-1">Locație</p>
                    <p className="text-white font-bold text-sm">{selectedResult.subscription?.location || '-'}</p>
                  </div>
                  {selectedResult.subscription?.sessions_remaining != null && (
                    <div className={`p-3 border col-span-2 text-center ${selectedResult.subscription.sessions_remaining <= 2 ? 'bg-yellow-500/5 border-yellow-500/20' : 'bg-[#6db025]/5 border-[#6db025]/20'}`}>
                      <p className={`font-heading text-4xl font-black ${selectedResult.subscription.sessions_remaining <= 2 ? 'text-yellow-400' : 'text-[#6db025]'}`}>
                        {selectedResult.subscription.sessions_remaining}
                        <span className="text-lg ml-2 font-normal opacity-60">/ {selectedResult.subscription?.sessions_total}</span>
                      </p>
                      <p className="text-white/30 text-[10px] uppercase font-black tracking-widest mt-1">Ședințe Rămase</p>
                    </div>
                  )}
                  {selectedResult.subscription?.expires_at && !selectedResult.subscription?.sessions_total && (
                    <div className="p-3 bg-white/[0.03] border border-white/5 col-span-2 text-center">
                      <p className="text-white font-bold text-lg">{new Date(selectedResult.subscription.expires_at).toLocaleDateString('ro-RO')}</p>
                      <p className="text-white/30 text-[10px] uppercase font-black tracking-widest mt-1">Data Expirării</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      <div className={`grid lg:grid-cols-2 gap-6 ${mode === 'verify' ? 'hidden' : ''}`}>
        {/* Partea de Input / Video */}
        <div className="bg-[#0A0A0A] border border-white/5 p-6 space-y-6">
          <div>
            <label className="text-white/50 text-xs font-bold uppercase tracking-wider mb-2 block">Locația Curentă</label>
            <select
              value={selectedLocation}
              onChange={(e) => setSelectedLocation(e.target.value)}
              className="w-full bg-[#121212] border border-white/10 px-4 py-4 text-white focus:border-[#6db025] outline-none appearance-none"
            >
              {LOCATIONS.map((loc) => (
                <option key={loc.id} value={loc.name}>{loc.name}</option>
              ))}
            </select>
          </div>

          {cameraMode ? (
            <div className="space-y-4">
              <div className="aspect-video bg-[#121212] border border-white/10 relative overflow-hidden flex items-center justify-center">
                <video ref={videoRef} className="absolute inset-0 w-full h-full object-cover" muted playsInline />
                
                {/* Chenarul vizual pentru focus (HUD) */}
                <div className="absolute inset-0 pointer-events-none z-10">
                  <div className="w-full h-full border-[40px] border-black/50" />
                  <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-48 h-48 border-2 border-[#6db025] shadow-[0_0_15px_rgba(109,176,37,0.3)]">
                     <div className="absolute -top-1 -left-1 w-4 h-4 border-t-2 border-l-2 border-[#6db025] bg-[#121212] -translate-x-1/2 -translate-y-1/2" />
                     <div className="absolute -top-1 -right-1 w-4 h-4 border-t-2 border-r-2 border-[#6db025] bg-[#121212] translate-x-1/2 -translate-y-1/2" />
                     <div className="absolute -bottom-1 -left-1 w-4 h-4 border-b-2 border-l-2 border-[#6db025] bg-[#121212] -translate-x-1/2 translate-y-1/2" />
                     <div className="absolute -bottom-1 -right-1 w-4 h-4 border-b-2 border-r-2 border-[#6db025] bg-[#121212] translate-x-1/2 translate-y-1/2" />
                  </div>
                </div>

                {!cameraActive && !cameraError && (
                  <p className="text-white/30 text-xs uppercase tracking-widest relative z-20">Se inițializează camera...</p>
                )}
              </div>

              {cameraError ? (
                <div className="p-4 bg-red-500/10 border border-red-500/20 text-red-400 text-xs font-bold uppercase tracking-widest flex items-center gap-2">
                  <AlertTriangle size={16} /> {cameraError}
                </div>
              ) : (
                <p className="text-white/40 text-[10px] font-bold uppercase tracking-widest text-center">
                  {cameraActive ? 'Încadrează codul QR al clientului în centrul ecranului.' : 'Camera este oprită.'}
                </p>
              )}

              <div className="flex gap-2 pt-2">
                <button onClick={startCamera} className="flex-1 btn-primary py-4 text-xs tracking-widest flex items-center justify-center gap-2">
                  <RefreshCw size={14} /> Repornește 
                </button>
                <button onClick={stopCamera} className="flex-1 btn-secondary py-4 text-xs tracking-widest">
                  Oprește
                </button>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmitManual} className="space-y-4">
              <div>
                <label className="text-white/50 text-xs font-bold uppercase tracking-wider mb-2 block">
                  Token QR Manual
                </label>
                <textarea
                  value={qrInput}
                  onChange={(e) => setQrInput(e.target.value)}
                  onKeyDown={(e) => {
                    // Scanner hardware trimite Enter automat după cod - submitează direct
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      if (qrInput.trim()) processQrToken(qrInput.trim(), 'scanner');
                    }
                  }}
                  placeholder="Lipește textul codului sau folosește scanner-ul fizic..."
                  className="w-full bg-[#121212] border border-white/10 focus:border-[#6db025] px-4 py-4 text-white placeholder:text-white/20 h-32 resize-none font-mono text-sm outline-none transition-all"
                />
              </div>

              <div className="flex gap-2">
                <button type="button" onClick={handleValidateOnly} className="flex-1 btn-secondary py-4 text-[10px] font-black tracking-widest">
                  Verifică Token
                </button>
                <button type="submit" disabled={scanning || !qrInput.trim()} className="flex-1 btn-primary py-4 text-[10px] font-black tracking-widest disabled:opacity-20">
                  {scanning ? 'Se înregistrează...' : 'Marchează Prezența'}
                </button>
              </div>
            </form>
          )}
        </div>

        {/* Partea de Rezultate */}
        <div className="bg-[#0A0A0A] border border-white/5 p-6 flex flex-col">
          <h3 className="text-white font-black uppercase tracking-widest text-base sm:text-lg mb-6">Rezultat Interogare</h3>

          <div className="flex-1 flex flex-col items-center justify-center">
            {lastResult ? (
              lastResult.success ? (
                <div className="w-full space-y-6 text-center animate-fade-in">
                  <div className="w-20 h-20 bg-green-500/10 rounded-full flex items-center justify-center mx-auto border border-green-500/20">
                    <Check size={32} className="text-green-500" />
                  </div>
                  <div>
                    <p className="text-white text-2xl font-heading font-bold uppercase tracking-tight">{lastResult.person_name}</p>
                    <span className="inline-block mt-2 text-[10px] uppercase font-black px-3 py-1 bg-white/5 text-white/40 tracking-widest">
                      {lastResult.person_type === 'child' ? 'Copil' : 'Adult / Titular'}
                    </span>
                  </div>

                  {lastResult.sessions_remaining !== null && (
                    <div className={`p-6 mt-4 border ${
                      lastResult.sessions_remaining <= 2 ? 'bg-yellow-500/5 border-yellow-500/20' : 'bg-white/[0.02] border-white/5'
                    }`}>
                      <p className={`font-heading text-5xl font-black mb-1 ${
                        lastResult.sessions_remaining <= 2 ? 'text-yellow-400' : 'text-[#6db025]'
                      }`}>
                        {lastResult.sessions_remaining}
                      </p>
                      <p className="text-white/40 text-[10px] font-bold uppercase tracking-widest">Ședințe Disponibile</p>
                    </div>
                  )}

                  {lastResult.warnings?.length > 0 && (
                    <div className="space-y-2 mt-4 text-left">
                      {lastResult.warnings.map((warning, i) => (
                        <div key={i} className="flex items-center gap-3 p-4 bg-yellow-500/5 border border-yellow-500/20">
                          <AlertTriangle size={16} className="text-yellow-500 flex-shrink-0" />
                          <span className="text-yellow-500 text-xs font-bold uppercase">{warning}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div className="w-full text-center space-y-4 animate-fade-in">
                  <div className="w-20 h-20 bg-red-500/10 rounded-full flex items-center justify-center mx-auto border border-red-500/20">
                    <X size={32} className="text-red-500" />
                  </div>
                  <h4 className="text-white font-heading text-xl uppercase font-bold tracking-tight">Acces Respins</h4>
                  <p className="text-red-400 text-sm font-medium p-4 bg-red-500/5 border border-red-500/10">{lastResult.error}</p>
                </div>
              )
            ) : (
              <div className="text-center opacity-30">
                <QrCode size={64} className="mx-auto mb-6 text-white" />
                <p className="text-white text-xs font-bold uppercase tracking-widest">Sistem pregătit.</p>
                <p className="text-white text-xs mt-1">Aștept scanarea token-ului...</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {kiosk && createPortal(
        <KioskView
          scanning={scanning}
          selectedLocation={selectedLocation}
          voiceEnabled={voiceEnabled}
          onToggleVoice={() => setVoiceEnabled((v) => !v)}
          onExit={exitKiosk}
          onLockChange={(locked) => { kioskLockedRef.current = locked; }}
        />,
        document.body,
      )}
    </div>
  );
};

export default QRScanPage;